"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/shared/lib/utils";
import { buttonVariants } from "@/shared/components/ui/button";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "p-3 bg-[#0A1628] text-gray-200 rounded-lg shadow-lg", // ← full dark navy background
        className
      )}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 bg-[#0A1628] text-gray-200 rounded-lg", // ← each month bg dark
        caption: "flex justify-center pt-1 relative items-center text-gray-200",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent text-gray-200 opacity-50 hover:opacity-100 border-cyan-500/20 hover:bg-cyan-500/10 transition"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-cyan-400 rounded-md w-8 font-normal text-[0.8rem] text-center",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal text-gray-200 hover:bg-cyan-500/10 hover:text-cyan-400 focus:bg-cyan-500/20 focus:text-cyan-400 aria-selected:bg-cyan-500 aria-selected:text-gray-50"
        ),
        day_range_start: "day-range-start rounded-l-md bg-cyan-500/20 text-gray-50",
        day_range_end: "day-range-end rounded-r-md bg-cyan-500/20 text-gray-50",
        day_selected:
          "bg-cyan-500 text-gray-50 hover:bg-cyan-600 hover:text-gray-50 focus:bg-cyan-600 focus:text-gray-50",
        day_today:
          "bg-cyan-400/20 text-cyan-400 hover:bg-cyan-500/30 hover:text-cyan-50",
        day_outside:
          "text-gray-500 aria-selected:bg-cyan-500/10 aria-selected:text-gray-200",
        day_disabled: "text-gray-600 opacity-50",
        day_range_middle:
          "aria-selected:bg-cyan-500/10 aria-selected:text-gray-200",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft
            className={cn("h-4 w-4 text-gray-200", className)}
            {...props}
          />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight
            className={cn("h-4 w-4 text-gray-200", className)}
            {...props}
          />
        ),
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
